// src/pages/dealerWarehouse/hooks/useDealerWarehouseData.js
import { useCallback, useEffect, useMemo, useState } from "react";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import {
  useGetWarehousesQuery,
  useCreateWarehouseMutation,
  useUpdateWarehouseMutation,
  useDeleteWarehouseMutation,
  useGetWarehouseTypesQuery,
  useGetParentWarehousesQuery
} from "../../../api/endpoints/dealerWarehouse";
import { useLanguage } from "../../../contexts/LanguageContext";
import { toastService } from "../../../utils/toastService";
import { useSelector } from "react-redux";
import { selectUserParams } from "../../../store/slices/userParamsSlice";
import { useGetUserParamsQuery } from "../../../api/endpoints/user";

const useDealerWarehouseData = () => {
  const { th, getCurrentLanguageId } = useLanguage();

  const { data: userParamsResp } = useGetUserParamsQuery();
  const userParamsFromStore = useSelector(selectUserParams);
  const userParams = userParamsFromStore || userParamsResp || {};
  const appBranchId = userParams?.dealerId ?? 0;

  const languageId = getCurrentLanguageId();

  const ready = Boolean(appBranchId && languageId);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({ PageNumber: 1, PageSize: 10 });

  const filterFieldNames = useMemo(
    () => ["warehouseName", "warehouseTypeId", "parentId", "isActive"],
    []
  );
  const createInitialFilters = useCallback(() => {
    const f = {};
    for (const field of filterFieldNames) {
      if (field === "isActive")
        f[field] = { value: true, matchMode: FilterMatchMode.EQUALS };
      else if (field === "warehouseTypeId" || field === "parentId")
        f[field] = { value: null, matchMode: FilterMatchMode.EQUALS };
      else
        f[field] = {
          operator: FilterOperator.AND,
          constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }]
        };
    }
    f.global = { value: null, matchMode: FilterMatchMode.CONTAINS };
    return f;
  }, [filterFieldNames]);

  const [filters, setFilters] = useState(createInitialFilters());

  const activeStatusValue = useMemo(
    () =>
      filters.isActive?.value === undefined
        ? undefined
        : filters.isActive?.value
        ? 1
        : 0,
    [filters.isActive?.value]
  );

  const queryParams = useMemo(
    () => ({
      appBranchId,
      languageId,
      pageNumber: pagination.PageNumber,
      pageSize: pagination.PageSize,
      search: filters.warehouseName?.constraints?.[0]?.value || "",
      parentId: filters.parentId?.value ?? null,
      warehouseTypeId: filters.warehouseTypeId?.value ?? null,
      isActive: activeStatusValue
    }),
    [appBranchId, languageId, pagination, filters, activeStatusValue]
  );

  const {
    data,
    isLoading: isLoadingList,
    isFetching,
    error: listError,
    refetch
  } = useGetWarehousesQuery(queryParams, {
    skip: !ready,
    refetchOnMountOrArgChange: true
  });

  const { data: typeItems = [] } = useGetWarehouseTypesQuery(
    { languageId },
    { skip: !languageId, refetchOnMountOrArgChange: false }
  );

  const { data: parentItems = [] } = useGetParentWarehousesQuery(
    { appBranchId, languageId, pageNumber: 1, pageSize: 1000, isActive: 1 },
    { skip: !ready, refetchOnMountOrArgChange: false }
  );

  const [createWarehouse, { isLoading: isCreating }] = useCreateWarehouseMutation();
  const [updateWarehouse, { isLoading: isUpdating }] = useUpdateWarehouseMutation();
  const [deleteWarehouse, { isLoading: isDeleting }] = useDeleteWarehouseMutation();

  useEffect(() => {
    const paged = data?.rows
      ? data
      : data?.data?.rows
      ? data.data
      : { rows: [], totalCount: 0, pageItemCount: 0 };

    setRows(Array.isArray(paged.rows) ? paged.rows : []);
    setTotalCount(paged.totalCount || 0);
  }, [data]);

  useEffect(() => {
    setLoading(isLoadingList || isFetching || !ready);
  }, [isLoadingList, isFetching, ready]);

  const handleFilterChange = useCallback(
    (updatedFilters) => {
      setFilters((prev) => {
        const next = { ...prev };

        if ("warehouseName" in updatedFilters) {
          next.warehouseName = {
            operator: FilterOperator.AND,
            constraints: [
              {
                value: updatedFilters.warehouseName || null,
                matchMode: FilterMatchMode.STARTS_WITH
              }
            ]
          };
        }
        if ("warehouseTypeId" in updatedFilters) {
          next.warehouseTypeId = {
            value: updatedFilters.warehouseTypeId ?? null,
            matchMode: FilterMatchMode.EQUALS
          };
        }
        if ("parentId" in updatedFilters) {
          next.parentId = {
            value: updatedFilters.parentId ?? null,
            matchMode: FilterMatchMode.EQUALS
          };
        }
        if ("isActive" in updatedFilters) {
          next.isActive = {
            value: !!updatedFilters.isActive,
            matchMode: FilterMatchMode.EQUALS
          };
        }
        return next;
      });
      setPagination((p) => ({ ...p, PageNumber: 1 }));
      setTimeout(() => refetch(), 50);
    },
    [refetch]
  );

  const handleApplyFilters = useCallback(
    (updated) => handleFilterChange(updated),
    [handleFilterChange]
  );

  const handleResetFilters = useCallback(() => {
    setFilters(createInitialFilters());
    setPagination((p) => ({ ...p, PageNumber: 1 }));
    setTimeout(() => refetch(), 50);
  }, [createInitialFilters, refetch]);

  const handleSearch = useCallback(
    (text) => handleFilterChange({ warehouseName: text || "" }),
    [handleFilterChange]
  );

  const onPageChange = useCallback((event) => {
    const newPageNumber = Math.floor(event.first / event.rows) + 1;
    setPagination((prev) => ({
      ...prev,
      PageNumber: newPageNumber,
      PageSize: event.rows
    }));
  }, []);

  const handleDelete = async (row) => {
    try {
      await deleteWarehouse({ id: row.id, appBranchId }).unwrap();
      toastService.success(th("dealerWarehouse.toast.deleteSuccess") || "Ambar silindi.");
      refetch();
    } catch (e) {
      toastService.error(
        th("dealerWarehouse.toast.deleteError") || "Silme işlemi başarısız."
      );
    }
  };

  const saveWarehouse = async (payload, isEdit, current) => {
    try {
      const withContext = { ...payload, appBranchId, languageId };

      if (isEdit && current) {
        await updateWarehouse(withContext).unwrap();
        toastService.success(
          th("dealerWarehouse.updateSuccess") || "Ambar güncellendi."
        );
      } else {
        await createWarehouse(withContext).unwrap();
        toastService.success(
          th("dealerWarehouse.createSuccess") || "Ambar oluşturuldu."
        );
      }
      refetch();
      return true;
    } catch (e) {
      console.error(e);
      toastService.error(
        th("dealerWarehouse.saveError") || "Kayıt sırasında hata oluştu."
      );
      return false;
    }
  };

  const typeOptions = useMemo(() => {
    const arr = typeItems?.data || typeItems || [];
    return arr.map((x) => ({
      label: x.translatedText || x.text || x.label,
      value: Number(x.code ?? x.value ?? x.id)
    }));
  }, [typeItems]);

  const parentOptions = useMemo(() => {
    const arr = parentItems?.data || parentItems || [];
    return arr.map((x) => ({
      label: x.warehouseName || x.text || x.label,
      value: x.id
    }));
  }, [parentItems]);

  const filterFields = useMemo(
    () => [
      {
        field: "warehouseName",
        headerName: th("dealerWarehouse.filters.warehouseName"),
        filterPlaceholder: th("dealerWarehouse.warehouseNamePlaceholder"),
        type: "text"
      },
      {
        field: "warehouseTypeId",
        headerName: th("dealerWarehouse.filters.type"),
        filterPlaceholder: th("dealerWarehouse.typePlaceholder"),
        type: "select",
        options: typeOptions
      },
      {
        field: "parentId",
        headerName: th("dealerWarehouse.filters.parent"),
        filterPlaceholder: th("dealerWarehouse.parentPlaceholder"),
        type: "select",
        options: parentOptions
      },
      {
        field: "isActive",
        headerName: th("dealerWarehouse.filters.isActive"),
        type: "boolean"
      }
    ],
    [th, typeOptions, parentOptions]
  );

  const customFilterTypes = useMemo(
    () => ({
      isActive: "boolean",
      warehouseTypeId: "select",
      parentId: "select",
      warehouseName: "text"
    }),
    []
  );

  const customFilterOptions = useMemo(
    () => ({
      isActive: [
        { label: th("common.active") || "Aktif", value: true },
        { label: th("common.inactive") || "Pasif", value: false }
      ]
    }),
    [th]
  );

  return {
    rows,
    loading,
    totalCount,
    pagination,
    filters,
    isCreating,
    isUpdating,
    isDeleting,
    filterFields,
    customFilterTypes,
    customFilterOptions,
    handleDelete,
    saveWarehouse,
    handleSearch,
    handleFilterChange,
    handleResetFilters,
    handleApplyFilters,
    onPageChange
  };
};

export default useDealerWarehouseData;
